import { formatPercent } from "../format"

import type { BigintIsh } from "./BigintIsh"
import Fraction from "./Fraction"
import type Rounding from "./Rounding"

const ONE_HUNDRED = new Fraction(100n)

/**
 * Converts a fraction to a percent
 * @param fraction the fraction to convert
 * @returns Percent the fraction as a percent
 */
function toPercent(fraction: Fraction): Percent {
  return new Percent(fraction.numerator, fraction.denominator)
}

/**
 * The `Percent` class represents a fractional value as a percentage.
 * It extends the `Fraction` class and provides methods for formatting values as percentages.
 */
class Percent extends Fraction {
  /**
   * This boolean flag indicates that this class represents a percentage value.
   * @type {true}
   */
  readonly isPercent = true as const

  /**
   * Adds another `Fraction` or a `BigintIsh` value to this `Percent` and returns the result as a `Percent`.
   * @param {Fraction | BigintIsh} other - The value to add.
   * @returns {Percent} - A new `Percent` instance representing the result of the addition.
   */
  override add(other: Fraction | BigintIsh): Percent {
    return toPercent(super.add(other))
  }

  /**
   * Subtracts another `Fraction` or a `BigintIsh` value from this `Percent` and returns the result as a `Percent`.
   * @param {Fraction | BigintIsh} other - The value to subtract.
   * @returns {Percent} - A new `Percent` instance representing the result of the subtraction.
   */
  override subtract(other: Fraction | BigintIsh): Percent {
    return toPercent(super.subtract(other))
  }

  /**
   * Multiplies this `Percent` by another `Fraction` or a `BigintIsh` value and returns the result as a `Percent`.
   * @param {Fraction | BigintIsh} other - The value to multiply by.
   * @returns {Percent} - A new `Percent` instance representing the result of the multiplication.
   */
  override multiply(other: Fraction | BigintIsh): Percent {
    return toPercent(super.multiply(other))
  }

  /**
   * Divides this `Percent` by another `Fraction` or a `BigintIsh` value and returns the result as a `Percent`.
   * @param {Fraction | BigintIsh} other - The value to divide by.
   * @returns {Percent} - A new `Percent` instance representing the result of the division.
   */
  override divide(other: Fraction | BigintIsh): Percent {
    return toPercent(super.divide(other))
  }

  /**
   * Converts the `Percent` into a string representation with a specified number of significant digits.
   * @param {number} significantDigits - The number of significant digits to display.
   * @param {object} format - Optional formatting options for the string representation.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Percent` as a percentage value.
   * @override
   */
  override toSignificant(
    significantDigits = 5,
    format?: object,
    rounding?: Rounding,
  ): string {
    return super
      .multiply(ONE_HUNDRED)
      .toSignificant(significantDigits, format, rounding)
  }

  /**
   * Converts the `Percent` into a string representation with a fixed number of decimal places.
   * @param {number} decimalPlaces - The number of decimal places to display.
   * @param {object} format - Optional formatting options for the string representation.
   * @param {Rounding} rounding - The rounding mode to use.
   * @returns {string} - A string representation of the `Percent` as a percentage value.
   * @override
   */
  override toFixed(
    decimalPlaces = 2,
    format?: object,
    rounding?: Rounding,
  ): string {
    return super.multiply(ONE_HUNDRED).toFixed(decimalPlaces, format, rounding)
  }

  /**
   * Converts the `Percent` into a string representation with a specified number of decimal places,
   * suitable for displaying as a percentage (e.g., "50%").
   * @param {number} decimalPlaces - The number of decimal places to display.
   * @returns {string} - A string representation of the `Percent` as a percentage value.
   */
  toPercentageString(decimalPlaces = 2): string {
    // +2 since 0.5 = 50%
    return formatPercent(super.toFixed(decimalPlaces + 2))
  }
}

export default Percent
